/**
 * Landing Page - Public Homepage
 * Welcoming homepage for potential customers
 * Showcases features without requiring authentication
 * Red/Black/White Theme
 */

import { useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import {
  RefreshCw,
  DollarSign,
  BarChart3,
  Handshake,
  ScanLine,
  Store,
  Shuffle,
  Sparkles,
  Download,
  Rocket,
  Check,
  X,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '../../../components/ui'
import { useCurrency } from '../../../contexts/CurrencyContext'
import { buildApiNavigationUrl } from '../../../utils/apiNavigation'
import { pickBestPriceCents } from '../../../utils/price'
import { usePlanCatalog } from '../../../hooks/usePlanCatalog'
import {
  normalizePlanId,
  PUBLIC_PLAN_ORDER,
} from '../../../services/subscriptionPlans.service'
import styles from './Landing.module.css'

interface CardVariant {
  printing: string
  condition: string
  priceCents: number
}

interface ShowcaseCard {
  id: string
  name: string
  setName: string
  setNumber: string
  imageUrl: string
  variants: CardVariant[]
}

const formatInt = (n: number) => new Intl.NumberFormat().format(Math.max(0, Math.round(n)))

const isPopularTeaserPlan = (id: string) => normalizePlanId(id) === 'professional'

export const Landing = () => {
  const navigate = useNavigate()
  const { formatPrice } = useCurrency()
  const showcaseAttemptRef = useRef(0)
  const MAX_SHOWCASE_ATTEMPTS = 5
  const [showcaseCard, setShowcaseCard] = useState<ShowcaseCard | null>(null)
  const [isLoadingCard, setIsLoadingCard] = useState(true)
  const [selectedPrinting, setSelectedPrinting] = useState('')
  const [selectedCondition, setSelectedCondition] = useState('')
  const [zoomedImage, setZoomedImage] = useState<string | null>(null)
  const {
    plans: pricingPlans,
    loading: pricingPlansLoading,
  } = usePlanCatalog()

  // Fetch a random high-value card from the database
  const fetchRandomCard = async (options?: { userInitiated?: boolean; excludeCardId?: string }) => {
    if (options?.userInitiated) {
      showcaseAttemptRef.current = 0
    }
    try {
      setIsLoadingCard(true)

      // IMPORTANT: Landing page must work when logged out.
      // This endpoint is explicitly public and returns a pre-filtered showcase card.
      const params = new URLSearchParams({ minPriceCents: '20000' })
      if (options?.userInitiated) {
        params.set('refresh', '1')
        params.set('seed', `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`)
      }
      if (options?.excludeCardId) {
        params.set('excludeCardId', options.excludeCardId)
      }
      const response = await fetch(`/api/public/cards/showcase?${params.toString()}`, {
        method: 'GET',
        cache: 'no-store'
      })

      if (!response.ok) {
        throw new Error(`Showcase fetch failed (${response.status})`)
      }

      const payload = await response.json()
      const card = payload?.data?.card

      if (!payload?.success || !card) {
        throw new Error('Invalid showcase response')
      }

      const variants: CardVariant[] = (card.variants || [])
        .map((v: any) => ({
          printing: v.printing || 'Normal',
          condition: v.condition || 'Near Mint',
          priceCents: pickBestPriceCents(v?.priceCents, v?.price)
        }))
        .filter((v: CardVariant) => v.priceCents >= 20000)

      if (!card.imageUrl || variants.length === 0) {
        showcaseAttemptRef.current += 1
        if (showcaseAttemptRef.current >= MAX_SHOWCASE_ATTEMPTS) {
          setShowcaseCard(null)
          return
        }
        setTimeout(() => void fetchRandomCard(), 1000)
        return
      }

      showcaseAttemptRef.current = 0

      const showcaseData: ShowcaseCard = {
        id: String(card.id),
        name: card.name,
        setName: card.setName || 'Unknown Set',
        setNumber: card.setNumber || '',
        imageUrl: card.imageUrl,
        variants
      }
      
      setShowcaseCard(showcaseData)
      
      // Set initial selections
      if (variants.length > 0) {
        setSelectedPrinting(variants[0].printing)
        setSelectedCondition(variants[0].condition)
      }
      
    } catch (error) {
      console.error('Failed to fetch showcase card:', error)
      setShowcaseCard(null)
    } finally {
      setIsLoadingCard(false)
    }
  }

  // Load a random card on mount
  useEffect(() => {
    void fetchRandomCard()
  }, [])

  const availablePrintings = showcaseCard
    ? [...new Set(showcaseCard.variants.map((variant: CardVariant) => variant.printing))]
    : []
  const availableConditions = showcaseCard
    ? [...new Set(showcaseCard.variants.map((variant: CardVariant) => variant.condition))]
    : []

  // Get current price based on combined printing + condition
  const getCurrentPrice = () => {
    if (!showcaseCard) return 0
    const variant = showcaseCard.variants.find(
      (v: CardVariant) => v.printing === selectedPrinting && v.condition === selectedCondition
    )
    return variant ? variant.priceCents : 0
  }

  const formatAudUnits = (amount: number) => formatPrice(Math.round(amount * 100), 'AUD').replace(/\.00$/, '')

  const paidPricingPlans = pricingPlans
    .filter((p) => ['basic', 'professional', 'enterprise'].includes(normalizePlanId(p.id)))
    .sort(
      (a, b) =>
        (PUBLIC_PLAN_ORDER[normalizePlanId(a.id)] ?? 999) - (PUBLIC_PLAN_ORDER[normalizePlanId(b.id)] ?? 999)
    )

  const paidMonthlyPrices = paidPricingPlans
    .map((p) => p.price)
    .filter((p): p is number => p != null && p > 0)
  const lowestPaidMonthly = paidMonthlyPrices.length > 0 ? Math.min(...paidMonthlyPrices) : null

  // If Shopify loads the app URL with ?shop=..., route into the Shopify launch handler.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const shopParam = params.get('shop') || params.get('shop_domain') || params.get('shopDomain')
    if (shopParam) {
      const normalized = shopParam
        .trim()
        .replace(/^https?:\/\//i, '')
        .split('/')[0]
        .toLowerCase()

      if (/\.myshopify\.com$/i.test(normalized)) {
        window.location.href = buildApiNavigationUrl('/shopify/launch', { shop: normalized })
      }
    }
  }, [])

  const features: { Icon: LucideIcon; title: string; description: string }[] = [
    {
      Icon: RefreshCw,
      title: 'Sell Online',
      description: 'Push cards to your Shopify store with images and prices. TCGEasy keeps one stock count, so orders update it and your listings follow.'
    },
    {
      Icon: DollarSign,
      title: 'Price Automatically',
      description: 'Set your rules once. Prices follow the market and update your listings, so you can list thousands of cards without doing it by hand.'
    },
    {
      Icon: Store,
      title: 'Sell In Store',
      description: 'Ring up in-store sales from the same stock as your website. Customers can scan QR labels to see prices and build a basket.'
    },
    {
      Icon: Handshake,
      title: 'Buy, Trade, and Consign',
      description: 'Work out trade-ins with current prices, and track consigned stock with statements and payouts for each consignor.'
    },
    {
      Icon: ScanLine,
      title: 'Scan Cards (Beta)',
      description: 'Point your phone at a card to identify it, or scan a whole pile in batch mode, then review it and add it to your inventory.'
    },
    {
      Icon: BarChart3,
      title: 'Know Your Numbers',
      description: 'See sales, margins, inventory value, and top products across online and in-store, and export your sales as CSV.'
    }
  ]

  const benefits = [
    {
      stat: '6',
      title: 'Games',
      description: 'Pokémon, Magic, One Piece, and more'
    },
    {
      stat: 'Auto',
      title: 'Pricing',
      description: 'Rules applied to market prices'
    },
    {
      stat: 'Shopify',
      title: 'Sync',
      description: 'Prices and stock pushed for you'
    },
    {
      stat: 'AU',
      title: 'Based',
      description: 'Built and supported in Australia'
    }
  ]

  const steps: { number: string; title: string; description: string; Icon: LucideIcon }[] = [
    {
      number: '1',
      title: 'Create Your Account',
      description: 'Sign up in seconds and explore TCGEasy on the free Starter plan. No credit card required.',
      Icon: Sparkles,
    },
    {
      number: '2',
      title: 'Add Your Inventory',
      description: 'Choose a paid plan, then bulk import cards via CSV, search and add them, or use the scanner.',
      Icon: Download,
    },
    {
      number: '3',
      title: 'Connect & Automate',
      description: 'Link your Shopify store and let TCGEasy handle pricing updates, inventory sync, and market tracking automatically.',
      Icon: Rocket,
    },
  ]

  return (
    <div className={styles.container}>
      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className={styles.zoomModal} onClick={() => setZoomedImage(null)}>
          <div className={styles.zoomContent}>
            <img
              src={zoomedImage}
              alt="Zoomed card"
              width={280}
              height={392}
              className={styles.zoomedImage}
            />
            <button type="button" className={styles.closeZoom} onClick={() => setZoomedImage(null)} aria-label="Close zoomed image">
              <X size={22} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            Run Your Card Store From <span className={styles.highlight}>One System</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Inventory, automated pricing, Shopify sync, and in-store sales, built for trading card
            stores. List more cards online without spending your week updating prices.
          </p>
          <div className={styles.heroCta}>
            <Button 
              variant="primary" 
              size="large"
              onClick={() => navigate('/signup')}
            >Start Free
            </Button>
            <Button 
              variant="secondary" 
              size="large"
              onClick={() => navigate('/features')}
            >
              Explore Features
            </Button>
          </div>
          <p className={styles.heroNote}>
            <span className={styles.heroNoteItem}>
              <Check size={15} strokeWidth={2.5} className={styles.heroNoteCheck} aria-hidden />
              No credit card required
            </span>
            <span className={styles.heroNoteSep} aria-hidden>
              •
            </span>
            <span className={styles.heroNoteItem}>
              <Check size={15} strokeWidth={2.5} className={styles.heroNoteCheck} aria-hidden />
              Free plan to explore
            </span>
            <span className={styles.heroNoteSep} aria-hidden>
              •
            </span>
            <span className={styles.heroNoteItem}>
              <Check size={15} strokeWidth={2.5} className={styles.heroNoteCheck} aria-hidden />
              No percentage of your sales
            </span>
          </p>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className={styles.benefitsBar}>
        <div className={styles.benefitsContent}>
          {benefits.map((benefit, index) => (
            <div key={index} className={styles.benefitItem}>
              <div className={styles.benefitStat}>{benefit.stat}</div>
              <div className={styles.benefitTitle}>{benefit.title}</div>
              <div className={styles.benefitDescription}>{benefit.description}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className={styles.featuresSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>What You Can Do With TCGEasy</h2>
          <p className={styles.sectionSubtitle}>
            Supports Pokémon (English and Japanese), Magic: The Gathering, One Piece, Dragon Ball Super, Riftbound, and Cyberpunk
          </p>
          <div className={styles.featuresGrid}>
            {features.map((feature, index) => {
              const { Icon, title, description } = feature
              return (
                <div key={index} className={styles.featureCard}>
                  <div className={styles.featureIcon} aria-hidden="true">
                    <Icon size={26} strokeWidth={1.75} />
                  </div>
                  <h3 className={styles.featureTitle}>{title}</h3>
                  <p className={styles.featureDescription}>{description}</p>
                </div>
              )
            })}
          </div>
          <div className={styles.featuresCtaContainer}>
            <Button 
              variant="primary"
              size="large"
              onClick={() => navigate('/features')}
            >
              See All Features
            </Button>
          </div>
        </div>
      </section>

      {/* Card Showcase Section */}
      <section className={styles.cardShowcaseSection}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>See Market Pricing in Action</h2>
          <p className={styles.sectionSubtitle}>
            Real cards from our database with current market pricing
          </p>
          
          {isLoadingCard ? (
            <div className={styles.showcaseLoading}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading premium card...</p>
            </div>
          ) : showcaseCard ? (
            <div className={styles.showcaseContainer}>
              <div className={styles.cardDisplay}>
                <img 
                  src={showcaseCard.imageUrl} 
                  alt={showcaseCard.name}
                  width={280}
                  height={392}
                  loading="lazy"
                  decoding="async"
                  className={styles.cardImage}
                  onClick={() => setZoomedImage(showcaseCard.imageUrl)}
                  title="Click to zoom"
                />
                <div className={styles.cardDetails}>
                  <h3 className={styles.cardName}>{showcaseCard.name}</h3>
                  <p className={styles.cardSet}>
                    {showcaseCard.setName}
                    {showcaseCard.setNumber && showcaseCard.setNumber !== 'N/A' ? ` #${showcaseCard.setNumber}` : ''}
                  </p>
                </div>
              </div>

              <div className={styles.priceControls}>
                {availablePrintings.length > 1 && (
                  <div className={styles.controlGroup}>
                    <label className={styles.controlLabel}>Select Printing:</label>
                    <div className={styles.buttonGroup}>
                      {availablePrintings.map((printing, index) => (
                        <button
                          key={index}
                          className={`${styles.controlButton} ${selectedPrinting === printing ? styles.active : ''}`}
                          onClick={() => setSelectedPrinting(printing)}
                        >
                          {printing}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {availableConditions.length > 1 && (
                  <div className={styles.controlGroup}>
                    <label className={styles.controlLabel}>Select Condition:</label>
                    <div className={styles.buttonGroup}>
                      {availableConditions.map((condition, index) => (
                        <button
                          key={index}
                          className={`${styles.controlButton} ${selectedCondition === condition ? styles.active : ''}`}
                          onClick={() => setSelectedCondition(condition)}
                        >
                          {condition}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.finalPriceDisplay}>
                  <div className={styles.priceBreakdown}>
                    <div className={styles.priceItem}>
                      <span className={styles.priceItemLabel}>Printing:</span>
                      <span className={styles.priceItemValue}>{selectedPrinting}</span>
                    </div>
                    <div className={styles.priceItem}>
                      <span className={styles.priceItemLabel}>Condition:</span>
                      <span className={styles.priceItemValue}>{selectedCondition}</span>
                    </div>
                  </div>
                  <div className={styles.totalPrice}>
                    <span className={styles.totalPriceLabel}>Current Price:</span>
                    <span className={styles.totalPriceValue}>{formatPrice(getCurrentPrice())}</span>
                  </div>
                </div>

                <Button 
                  variant="secondary"
                  size="medium"
                  onClick={() =>
                    void fetchRandomCard({
                      userInitiated: true,
                      excludeCardId: showcaseCard?.id,
                    })
                  }
                >
                  <Shuffle size={16} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} aria-hidden="true" />
                  Try Another Card
                </Button>
              </div>
            </div>
          ) : (
            <div className={styles.showcaseError}>
              <p>Unable to load card showcase. Please try again later.</p>
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howItWorks}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>How to Get Started</h2>
          <p className={styles.sectionSubtitle}>
            From first look to your store running on TCGEasy
          </p>
          <div className={styles.stepsGrid}>
            {steps.map((step, index) => {
              const { Icon } = step
              return (
                <div key={index} className={styles.step}>
                  <div className={styles.stepIcon} aria-hidden="true">
                    <Icon size={30} strokeWidth={1.75} />
                  </div>
                  <div className={styles.stepNumber}>{step.number}</div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className={styles.pricingTeaser}>
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Simple Monthly Pricing</h2>
          <p className={styles.sectionSubtitle}>
            {!pricingPlansLoading && lowestPaidMonthly != null ? (
              <>
                Flat monthly plans from {formatAudUnits(lowestPaidMonthly)}, with no percentage of your sales
              </>
            ) : (
              <>Flat monthly plans, with no percentage of your sales. See our pricing page for details.</>
            )}
          </p>
          <div className={styles.pricingCards}>
            {pricingPlansLoading ? (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center' }}>Loading plans…</p>
            ) : paidPricingPlans.length === 0 ? (
              <p style={{ gridColumn: '1 / -1', textAlign: 'center' }}>
                Unable to load plans right now.{' '}
                <Button type="button" variant="secondary" size="small" onClick={() => navigate('/pricing')}>
                  View pricing
                </Button>
              </p>
            ) : (
              paidPricingPlans.map((tier) => {
                const id = normalizePlanId(tier.id)
                const monthly = tier.price
                return (
                  <div key={tier.id} className={styles.pricingCard}>
                    {isPopularTeaserPlan(tier.id) ? <div className={styles.pricingBadge}>Recommended</div> : null}
                    <h3 className={styles.pricingPlan}>{tier.name}</h3>
                    <div className={styles.pricingPrice}>
                      <span className={styles.pricingAmount}>
                        {monthly != null && monthly > 0 ? formatAudUnits(monthly) : '—'}
                      </span>
                      <span className={styles.pricingPeriod}>/month</span>
                    </div>
                    <ul className={styles.pricingFeatures}>
                      <li>
                        <Check size={16} strokeWidth={2.5} className={styles.pricingFeatureCheck} aria-hidden />
                        <span>{formatInt(tier.maxProducts)} products</span>
                      </li>
                      <li>
                        <Check size={16} strokeWidth={2.5} className={styles.pricingFeatureCheck} aria-hidden />
                        <span>{formatInt(tier.maxUsers)} team members</span>
                      </li>
                      <li>
                        <Check size={16} strokeWidth={2.5} className={styles.pricingFeatureCheck} aria-hidden />
                        <span>
                          {tier.maxPlatforms} platform integration{tier.maxPlatforms === 1 ? '' : 's'}
                        </span>
                      </li>
                      {id === 'basic' ? (
                        <li>
                          <Check size={16} strokeWidth={2.5} className={styles.pricingFeatureCheck} aria-hidden />
                          <span>Automated price syncing</span>
                        </li>
                      ) : null}
                      {id === 'professional' ? (
                        <li>
                          <Check size={16} strokeWidth={2.5} className={styles.pricingFeatureCheck} aria-hidden />
                          <span>QR code generation</span>
                        </li>
                      ) : null}
                      {id === 'enterprise' ? (
                        <li>
                          <Check size={16} strokeWidth={2.5} className={styles.pricingFeatureCheck} aria-hidden />
                          <span>Our highest limits</span>
                        </li>
                      ) : null}
                    </ul>
                  </div>
                )
              })
            )}
          </div>
          <div className={styles.pricingCtaContainer}>
            <Button variant="primary" size="large" onClick={() => navigate('/pricing')}>
              View Full Pricing
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <h2 className={styles.ctaTitle}>Ready to See It for Your Store?</h2>
          <p className={styles.ctaDescription}>
            Create a free account and have a look around. No credit card required.
          </p>
          <Button 
            variant="primary" 
            size="large"
            onClick={() => navigate('/signup')}
          >
            Create a Free Account
          </Button>
          <p className={styles.ctaNote}>
            Adding inventory needs a paid plan • No lock-in contracts • 30-day refund on your first payment
          </p>
        </div>
      </section>
    </div>
  )
}

export default Landing
